/*
 * @Description: 配合BaseUpload使用，实现了自动注册字段与绑定目标字段。考虑是否要直接放到组件内部实现
 * @Date: 2025-05-23 14:55:33
 */
import { reactive, Ref, watchEffect } from 'vue';
import { XrkMessage } from 'xrk-components';

/**
 * @description: 注册组件绑定变量与事件，并监听初始化数据
 * @param {Ref} formData
 * @param {Ref} formRef
 * @param {Array<string>} uploadFieldNames 字段名集合，用于创建upload组件绑定对象的key与接口提交字段对应，可用于数组。
 *                        'fieldName' 普通的字段名
 *                        'fieldName.fieldName2' 数组的字段名，集合名字.集合中的url字段名
 *                        由watch完成数据健全
 * @return {Object}
 */
export const useUpload = (
  formData: Ref<Record<string, any>>,
  formRef: Ref<any>,
  uploadFieldNames: string[]
) => {
  // {a:[{url:''}] 普通字段 , b.1.c:[{url:'}] 集合字段  }，key与formData中的字段对应
  const uploadModelMap = reactive<Record<string, any>>({});

  /**
   * @description: 分割并解析当前字段名
   * @param {String} fieldName 字段组合名，.分割，如果不存在.则说明是普通字段，存在则说明是数组
   * @return {Array<string>} 如果length为2则说明是集合
   */
  const getSplitFieldName = fieldName => {
    return fieldName.split('.');
  };
  const setMapValue = (data?, fieldName?) => {
    if (fieldName && data[fieldName]) {
      return [
        {
          name: '',
          url: data[fieldName] || ''
        }
      ];
    }
    // 无值或无字段返回空数组
    return [];
  };
  /**
   * @description: 设置组件绑定的key，只有数组才会使用
   * @param {String} fieldName
   * @param {Nmuber} index
   * @return {String} a.0.b
   */
  const defMapKey = (fieldName, index) => {
    const [splitName, splitName2] = getSplitFieldName(fieldName);
    return `${splitName}.${index}.${splitName2}`;
  };

  watchEffect(() => {
    uploadFieldNames.forEach(uploadFieldNameItem => {
      const [splitName, splitName2] = getSplitFieldName(uploadFieldNameItem);
      const formDataFieldValue = formData.value[splitName];
      // 使用的上文为数组
      if (splitName2) {
        // 必有一个也相当于初始必。遍历后如果有值会覆盖掉
        uploadModelMap[defMapKey(uploadFieldNameItem, 0)] = setMapValue();
        // 遍历数组中的元素，给map赋值
        formDataFieldValue?.forEach((child, childIndex) => {
          uploadModelMap[defMapKey(uploadFieldNameItem, childIndex)] =
            setMapValue(child, splitName2);
        });
      } else {
        // string类型赋值与初始化
        uploadModelMap[splitName] = setMapValue(formData.value, splitName);
      }
    });
  });

  const onUploadErr = errorInfo => {
    XrkMessage.error(errorInfo.msg);
  };
  /**
   * @description: 组件值变化时，同步给formData的值
   * @param {Object} val 上传返回数据
   * @param {String} fieldName
   * @param {Number} index
   * @return {Void}
   */
  const onUploadChange = (val, fieldName, index?) => {
    if (val?.response?.data?.fileUrl) {
      const [splitName, splitName2] = getSplitFieldName(fieldName);
      if (splitName2) {
        const defKey = defMapKey(fieldName, index);
        formData.value[splitName][index][splitName2] =
          uploadModelMap[defKey]?.[0]?.url || '';
        formRef.value.validateField(defKey);
      } else {
        // 需要使用auto-upload
        formData.value[splitName] = uploadModelMap[splitName]?.[0]?.url || '';
        formRef.value.validateField(splitName);
      }
    }
  };
  const onUploadRemove = (fieldName, index?) => {
    const [splitName, splitName2] = getSplitFieldName(fieldName);
    if (splitName2) {
      const defKey = defMapKey(fieldName, index);
      formData.value[splitName][index][splitName2] = '';
      formRef.value.validateField(defKey);
    } else {
      // 需要使用auto-upload
      formData.value[splitName] = '';
      formRef.value.validateField(splitName);
    }
  };
  /**
   * @description: 新增上传内容，只能用于数组
   * @param {String} fieldName fieldName.fieldName2
   * @return {Void}
   */
  const onUploadArrAdd = fieldName => {
    const [splitName] = getSplitFieldName(fieldName);
    const fieldValue = formData.value[splitName];
    uploadModelMap[defMapKey(fieldName, fieldValue?.length || 0)] =
      setMapValue();
  };
  /**
   * @description: 新增上传内容，只能用于数组
   * @param {String} fieldName fieldName.fieldName2
   * @return {Void}
   */
  const onUploadArrRemove = (fieldName, fieldIndex) => {
    const [splitName] = getSplitFieldName(fieldName);
    delete uploadModelMap[defMapKey(fieldName, fieldIndex)];
    formData.value[splitName].splice(fieldIndex, 1);
  };

  return {
    uploadModelMap,
    onUploadErr,
    onUploadChange,
    onUploadRemove,
    onUploadArrAdd,
    onUploadArrRemove
  };
};
